/**
 * The same picture, twice.
 *
 * Upload dedupes on content hash, so byte-identical files never arrive twice.
 * Two scans of the same print do, and so does the same photograph at two
 * resolutions and a re-save that changed one JPEG coefficient — and an
 * inherited archive is full of all three, because somebody scans a box of
 * prints, finds it in a cupboard a year later, and scans it again.
 *
 * ## Nothing is deleted here
 *
 * Choosing a copy hides the other. The election between two scans is a
 * judgement that can be wrong — a smaller file is sometimes the better scan,
 * and the archive cannot see that — and "it threw away the good one" is not a
 * sentence this application should ever cause. Hidden is recoverable by
 * anybody; deleted is recoverable by nobody.
 */

import { h, replace } from "../dom";
import { api, blobUrl } from "../api";
import { store } from "../state";
import { empty, failure, loading, toast } from "../ui";
import { count } from "../format";
import type { DupeGroup, DupeMember } from "../types";
import type { View } from "./view";

export function mount(pane: HTMLElement): View {
  let generation = 0;

  function shot(m: DupeMember, best: boolean, onKeep: () => void): HTMLElement {
    const src = blobUrl(m.key);
    return h(
      "div",
      { class: best ? "dupshot best" : "dupshot" },
      h("img", {
        src: src ?? "",
        alt: m.fileName,
        loading: "lazy",
        onerror: (e: Event) => {
          (e.target as HTMLImageElement).style.visibility = "hidden";
        },
      }),
      h(
        "div",
        { class: "dupmeta" },
        h("div", { class: "nm", text: m.fileName }),
        h("div", {
          class: "d",
          text: `${m.width || "?"} × ${m.height || "?"}${
            m.distance !== undefined ? ` · ${m.distance} bits apart` : " · the better copy"
          }`,
        }),
      ),
      store.can("duplicate.resolve")
        ? h("button", { class: "btn sm", text: "Keep this one", onclick: onKeep })
        : null,
    );
  }

  function groupEl(g: DupeGroup, refresh: () => void): HTMLElement {
    const all = [g.representative, ...g.members];
    const wrap = h("div", { class: "dupgroup" });

    wrap.appendChild(
      h(
        "div",
        { class: "duphead" },
        h("span", {
          class: "kind",
          text: g.kind === "exact" ? "identical files" : "the same picture",
        }),
        h("span", {
          class: "muted",
          text:
            g.kind === "exact"
              ? "Byte for byte the same. Safe to keep one."
              : "Different files that look the same — a re-scan, a resize, a resave.",
        }),
      ),
    );

    const row = h("div", { class: "dupshots" });
    for (const m of all) {
      row.appendChild(
        shot(m, m.id === g.representative.id, () => {
          void (async () => {
            try {
              for (const other of all) {
                if (other.id === m.id) continue;
                await api.decideDupe(store.libraryId, m.id, other.id, "kept", m.id);
              }
              toast(`Kept ${m.fileName}. The others are hidden, not deleted.`);
              refresh();
            } catch (e) {
              toast(e instanceof Error ? e.message : "could not decide that", true);
            }
          })();
        }),
      );
    }
    wrap.appendChild(row);

    if (store.can("duplicate.resolve")) {
      wrap.appendChild(
        h("button", {
          class: "btn sm ghost",
          text: "These are different photographs",
          title: "Two pictures that happen to look alike — stop offering this pair",
          onclick: () => {
            void (async () => {
              try {
                for (const m of g.members) {
                  await api.decideDupe(store.libraryId, g.representative.id, m.id, "distinct");
                }
                toast("Noted. This pair will not come back.");
                refresh();
              } catch (e) {
                toast(e instanceof Error ? e.message : "could not save that", true);
              }
            })();
          },
        }),
      );
    }
    return wrap;
  }

  async function refresh(): Promise<void> {
    const mine = ++generation;
    replace(pane, loading("duplicates"));

    let found: { groups: DupeGroup[]; hashed: number };
    try {
      found = await api.dupes(store.libraryId);
    } catch (e) {
      if (mine !== generation) return;
      replace(pane, failure(e, () => void refresh()));
      return;
    }
    if (mine !== generation) return;

    if (!found.groups.length) {
      replace(
        pane,
        empty({
          icon: "album",
          title: found.hashed ? "No duplicates" : "Nothing has been compared yet",
          body: [
            found.hashed
              ? `${count(found.hashed, "photograph")} compared, and no two of them are ` +
                "the same picture. Scans of the same print are found here even when " +
                "the files differ."
              : "Photographs are compared as they arrive. If this archive predates " +
                "that, they can be compared now — it costs nothing but time.",
          ],
          actions: store.can("asset.add")
            ? [
                h("button", {
                  class: "btn primary",
                  text: "Compare everything",
                  onclick: () => {
                    void api
                      .rehash(store.libraryId)
                      .then((r) =>
                        toast(
                          r.queued
                            ? `Comparing ${count(r.queued, "photograph")}. Come back shortly.`
                            : "Everything has already been compared.",
                        ),
                      )
                      .catch((e: unknown) =>
                        toast(e instanceof Error ? e.message : "could not start that", true),
                      );
                  },
                }),
              ]
            : [],
        }),
      );
      return;
    }

    const head = h(
      "div",
      { class: "gridhead" },
      h("h2", { text: "The same picture, twice" }),
      h("p", {
        class: "sub",
        text:
          `${count(found.groups.length, "set")} out of ${count(found.hashed, "photograph")}. ` +
          "Choosing a copy hides the others — nothing here is deleted.",
      }),
    );
    const list = h("div", { class: "dupes" });
    for (const g of found.groups) list.appendChild(groupEl(g, () => void refresh()));
    replace(pane, head, list);
  }

  void refresh();
  return { refresh: () => void refresh(), destroy() { generation++; } };
}
